import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IApplyContractTermResult,
  useApplyContractModelDraftContractTermMutation,
} from "store/queries/contractModelDrafts";
import styled from "styled-components";

import {
  APPLY_ACTION_LABELS,
  IForValues,
  applyTargetRequest,
  describeApplyTarget,
  documentsLine,
  forValues,
  planWritesSomething,
  savedMessage,
  saveButtonLabel,
  stableStringify,
  summarizeApplyPlan,
} from "./applyTermPreview";

interface IApplyContractTermPanelProps {
  runId: string;
  /** The accepted `contract_term` recommendation. Null when there isn't one yet. */
  recommendationId: string | null;
  /**
   * CLE-2811 — that recommendation's values as they stand now (its `proposedModeling`). A preview is true
   * only for the values it was computed from: when these change, the preview and everything worked out
   * with it are cleared, and Save waits for a fresh dry run.
   */
  proposedModeling: unknown;
  /** The contract this run targets. Null means a new Draft contract would be created. */
  contractFeeScheduleId: string | null;
  /**
   * CLE-2811 — the term's payer as the agent named it. A run can hold one term per payer the document
   * priced, each with its own panel; this is what tells them apart before the preview has run.
   */
  termPayerName?: string | null;
  /**
   * Optional. The mutation already invalidates the run and the contract caches, so the page
   * refreshes on its own — this is only for a caller that wants to navigate afterwards.
   */
  onApplied?: (contractFeeScheduleId: string | null) => void;
}

/** A reimbursement slot can be a whole row-per-code schedule: keep it readable, never page-wide. */
const PreviewText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 12rem;
  overflow-y: auto;
`;

const FIELD_LABELS: Record<string, string> = {
  contractType: "Contract type",
  signedDate: "Signed date",
  timelyFilingDays: "Timely filing (days)",
  implantTerms: "Implant terms",
  inpatientReimbursement: "Inpatient reimbursement",
  outpatientReimbursement: "Outpatient reimbursement",
  facilityReimbursement: "Facility reimbursement",
  nonFacilityReimbursement: "Non-facility reimbursement",
  nextUpdateDate: "Next update date",
  updateTerm: "Update term",
  updatePercent: "Percent increase",
  notes: "Notes",
};

/**
 * CLE-2811 — write the agent's contract facts onto a real contract.
 *
 * Preview is a separate, mandatory step rather than a confirm dialog. This is the only control in
 * the tool that writes to a production pricing table, and the reviewer needs to see WHICH fields it
 * would touch before it touches them — a generic "are you sure?" tells her nothing about whether the
 * agent is about to fill a field she already answered.
 *
 * The one rule worth repeating in the UI: a field the contract already has is never overwritten. It
 * shows as "Left as is" so she can see the agent had a value and her own survived — unless she ticks
 * "Replace text the contract already has", which lets the free-text facts (implant terms, reimbursement)
 * overwrite a different value. Those show as "Will replace", with the text being replaced.
 *
 * WHERE it writes is the server's call, explained here (`target` on the dry run). One document often
 * prices several payers — the Westside run found Medflex, SuperMed and ACA, and the run belongs to the
 * Medflex contract. Medflex's term saves to the run's contract as always. SuperMed's term says so, and
 * offers SuperMed's existing contracts for this provider, or a new draft for SuperMed when there is none.
 */
export function ApplyContractTermPanel({
  runId,
  recommendationId,
  proposedModeling,
  contractFeeScheduleId,
  termPayerName,
  onApplied,
}: IApplyContractTermPanelProps) {
  const [applyContractTerm, { isLoading }] =
    useApplyContractModelDraftContractTermMutation();
  /**
   * CLE-2811 — the term's values, as a key. Everything the panel works out is stored with the values it was
   * worked out for and read back only while they still hold (`forValues`), so a change of values — she
   * re-confirmed with edits, a regeneration moved them, a facts-first schedule filled a slot — clears the
   * preview, the target, the contract picked from it, any error and a finished save: Save cannot write
   * values that were never previewed. A dry run that answers after the values moved is dropped the same way.
   */
  const valuesKey = stableStringify(proposedModeling);
  const latestValuesKey = useRef(valuesKey);
  useEffect(() => {
    latestValuesKey.current = valuesKey;
  }, [valuesKey]);
  /** The last dry run: what it would change, where it would write, and the documents a new contract would get. */
  const [lastDryRun, setLastDryRun] =
    useState<IForValues<IApplyContractTermResult> | null>(null);
  const [picked, setPicked] = useState<IForValues<string> | null>(null);
  const [failure, setFailure] = useState<IForValues<string> | null>(null);
  const [savedFor, setSavedFor] =
    useState<IForValues<IApplyContractTermResult> | null>(null);
  const [replace, setReplace] = useState(false);

  const planned = forValues(lastDryRun, valuesKey);
  const preview = planned ? planned.changes : null;
  const selectedContractId = forValues(picked, valuesKey);
  const error = forValues(failure, valuesKey);
  const saved = forValues(savedFor, valuesKey);

  if (!recommendationId) {
    return (
      <div className="mt-4 pt-3 border-top-1 surface-border">
        <Message
          severity="info"
          text="Accept the contract term above to save its details onto the contract."
        />
      </div>
    );
  }

  const runHasContract = !!contractFeeScheduleId;
  const view = planned
    ? describeApplyTarget(planned.target, runHasContract)
    : null;
  // Replace only means something on an EXISTING contract; a new draft has nothing to replace.
  const writesExisting = planned
    ? planned.target.mode !== "create"
    : runHasContract;

  const send = async (
    dryRun: boolean,
    {
      replaceText = replace,
      contractId = selectedContractId,
    }: { replaceText?: boolean; contractId?: string | null } = {}
  ) => {
    // The values this request is for: its answer counts only while they still hold.
    const sentFor = valuesKey;
    setFailure(null);
    try {
      const result = await applyContractTerm({
        runId,
        recommendationId,
        dryRun,
        replace: writesExisting && replaceText,
        // The FIRST preview sends no choice: the server proposes one. Every call after it — and every save —
        // names the choice explicitly, because the server refuses an off-payer save that does not.
        ...(view ? applyTargetRequest(view.choice, contractId) : {}),
      }).unwrap();
      if (dryRun) {
        setLastDryRun({ valuesKey: sentFor, value: result });
        if (contractId === null) {
          const next = describeApplyTarget(result.target, runHasContract);
          if (next.initialContractId)
            setPicked({ valuesKey: sentFor, value: next.initialContractId });
        }
      } else {
        // A save that happened is shown, whatever moved while it was in flight.
        setSavedFor({ valuesKey: latestValuesKey.current, value: result });
        setLastDryRun(null);
        onApplied?.(result.contractFeeScheduleId);
      }
    } catch (err: any) {
      setFailure({
        valuesKey: sentFor,
        value:
          err?.data?.message ??
          "Could not write the contract term to a contract.",
      });
    }
  };

  if (saved) {
    const docs = documentsLine(saved.target, saved.documents, true);
    return (
      <div className="mt-4 pt-3 border-top-1 surface-border">
        <Message
          severity="success"
          content={
            <div>
              <div>{savedMessage(saved)}</div>
              {saved.contractFeeScheduleId ? (
                <Link
                  className="block mt-1"
                  to={`/payer-rules/contracts/${saved.contractFeeScheduleId}`}
                >
                  Open {saved.target.contractName ?? "the contract"}
                </Link>
              ) : null}
              {docs ? <small className="block mt-1">{docs}</small> : null}
            </div>
          }
        />
      </div>
    );
  }

  const writesSomething = planWritesSomething(preview ?? []);
  const selectedName =
    planned?.target.existingCandidates.find((c) => c.id === selectedContractId)
      ?.name ?? null;
  const docs = planned
    ? documentsLine(planned.target, planned.documents, false)
    : null;

  // The preview is only true for the option it was computed with, so re-run it when she flips the box.
  const toggleReplace = (checked: boolean) => {
    setReplace(checked);
    if (preview !== null) send(true, { replaceText: checked });
  };

  // …and when she picks another contract to save to.
  const pickContract = (id: string) => {
    setPicked({ valuesKey, value: id });
    send(true, { contractId: id });
  };

  let heading = "Create a draft contract from these details";
  if (view && view.choice !== "default") {
    heading = `Save these details to a ${planned?.target.payerName} contract`;
  } else if (writesExisting) {
    heading = "Save these details to the contract";
  }
  const description = writesExisting
    ? "Fills only the fields the contract has left empty, and appends to its notes. Nothing already recorded is overwritten unless you tick “Replace text” below, and no rates change."
    : "Creates a Draft contract with these terms and no rate calculations. A draft prices nothing until someone activates it.";

  return (
    <div className="mt-4 pt-3 border-top-1 surface-border">
      <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-900 flex align-items-center gap-2 flex-wrap">
            {heading}
            {termPayerName ? (
              <Tag severity="info" value={termPayerName} />
            ) : null}
          </div>
          <small className="text-color-secondary">{description}</small>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            label="Preview changes"
            icon="pi pi-eye"
            className="p-button-outlined"
            loading={isLoading && preview === null}
            onClick={() => send(true)}
          />
          {preview !== null && planned && view && (
            <Button
              label={saveButtonLabel(planned.target, view.choice, selectedName)}
              icon="pi pi-check"
              disabled={
                !writesSomething ||
                (view.choice === "pick" && !selectedContractId)
              }
              tooltip={
                !writesSomething
                  ? "The contract already has every field the agent found"
                  : undefined
              }
              loading={isLoading}
              onClick={() => send(false)}
            />
          )}
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mt-3" />}

      {view?.notice ? (
        <div
          className="mt-3 p-3 border-round border-1"
          style={{
            background: "var(--yellow-50, #fefce8)",
            borderColor: "var(--yellow-300, #fcd34d)",
          }}
        >
          <div className="flex align-items-start gap-2">
            <i
              className="pi pi-exclamation-triangle mt-1"
              style={{ color: "var(--yellow-700, #a16207)" }}
            />
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div className="font-medium text-900">{view.notice}</div>
              {view.choice === "pick" ? (
                <div className="mt-2 flex align-items-center gap-2 flex-wrap">
                  <label
                    htmlFor={`apply-target-${recommendationId}`}
                    className="text-sm"
                  >
                    Save it to
                  </label>
                  <Dropdown
                    inputId={`apply-target-${recommendationId}`}
                    value={selectedContractId}
                    options={view.options}
                    disabled={isLoading}
                    onChange={(e) => pickContract(e.value)}
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              ) : (
                <small className="block mt-1 text-color-secondary">
                  This provider has no {planned?.target.payerName} contract yet.
                  Create a draft one to hold these details.
                </small>
              )}
              {docs ? (
                <small className="block mt-2 text-color-secondary">
                  {docs}
                </small>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {preview !== null && preview.length === 0 && (
        <Message
          severity="info"
          text="The agent recorded nothing that belongs on the contract."
          className="mt-3"
        />
      )}

      {preview !== null && preview.length > 0 && (
        <div className="mt-3 flex flex-column gap-2">
          <small className="text-color-secondary">
            {summarizeApplyPlan(preview)}
          </small>
          {!view?.notice && docs ? (
            <small className="text-color-secondary">{docs}</small>
          ) : null}
          {preview.map((change) => (
            <div
              key={change.field}
              className="flex align-items-start gap-3 p-2 surface-50 border-round"
            >
              <Tag
                value={APPLY_ACTION_LABELS[change.action].label}
                severity={APPLY_ACTION_LABELS[change.action].severity}
              />
              <div className="flex-1" style={{ minWidth: 0 }}>
                <div className="font-medium text-900">
                  {FIELD_LABELS[change.field] ?? change.field}
                </div>
                <div className="text-sm text-color-secondary">
                  {change.action === "skipped" && (
                    <PreviewText>
                      Keeping: {change.from ?? "not recorded"}
                    </PreviewText>
                  )}
                  {change.action === "replaced" && (
                    <>
                      <PreviewText>
                        <s>Replacing: {change.from ?? "not recorded"}</s>
                      </PreviewText>
                      <PreviewText className="mt-1 text-900">
                        With: {change.to ?? "not recorded"}
                      </PreviewText>
                    </>
                  )}
                  {(change.action === "filled" ||
                    change.action === "appended") && (
                    <PreviewText>
                      {change.from ? `${change.from} → ` : ""}
                      {change.to ?? "not recorded"}
                    </PreviewText>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {writesExisting && (
        <div className="mt-3 flex align-items-start gap-2">
          <Checkbox
            inputId={`replace-text-${recommendationId}`}
            checked={replace}
            disabled={isLoading}
            onChange={(e) => toggleReplace(!!e.checked)}
          />
          <label
            htmlFor={`replace-text-${recommendationId}`}
            className="cursor-pointer"
          >
            <span className="text-900">
              Replace text the contract already has
            </span>
            <small className="block text-color-secondary">
              Implant terms and reimbursement are overwritten with the
              agent&rsquo;s text. Dates, numbers and the contract type are still
              only filled when empty, and notes are still appended.
            </small>
          </label>
        </div>
      )}
    </div>
  );
}

export default ApplyContractTermPanel;
